import React, { useEffect, memo } from "react";

import { useVirtualizer } from "@tanstack/react-virtual";
import moment from "moment/moment";
import { IActivityDetails } from "@Models/IActivityDetails";
import ActivityListSkeleton from "@Components/activity/ActivityListSkeleton";
import ActivityIcon from "@Components/activity/ActivityIcon";
import VideoTag from "@Components/activity/VideoTag";
import { ILoadNextPage } from "@Components/shared/types";
import { IAssociatedStatuses } from "./hooks/useAssociatedStatuses";
import CMEStatus from "@Components/activity/CMEStatus";
import SessionManager from "@Core/session";
import { Empty } from "antd";

type IProps = {
    activities: IActivityDetails[];
    associatedStatusesMap: Map<string, IAssociatedStatuses>;
    filter: string;
    hasNextPage: boolean;
    isNextPageLoading: boolean;
    loadNextPage: ILoadNextPage;
    handleActivitySelect: (userId: string, activityInstanceId: string) => void;
};

const InfiniteActivityList: React.FC<IProps> = ({
    activities,
    filter,
    associatedStatusesMap,
    hasNextPage,
    isNextPageLoading,
    loadNextPage,
    handleActivitySelect,
}: IProps) => {
    const parentRef = React.useRef();
    const totalLength = activities.length;
    const isItemLoaded = (index: number) => !hasNextPage || index < activities.length;

    const rowVirtualizer = useVirtualizer({
        count: hasNextPage ? totalLength + 1 : totalLength,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 60,
        overscan: 5,
    });

    useEffect(() => {
        const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();
        if (!lastItem) {
            return;
        }
        if (lastItem.index >= totalLength - 1 && hasNextPage && !isNextPageLoading) {
            loadNextPage();
        }
    }, [hasNextPage, loadNextPage, totalLength, isNextPageLoading, rowVirtualizer.getVirtualItems()]);

    if (activities === undefined) return null;

    const RenderItem = (index: number) => {
        const activity: IActivityDetails = activities[index];
        const { userId } = SessionManager.getServiceUser();
        const isOwner = activity?.userId === userId;
        const isCMEPublic = activity?.activityMeta?.isCMEPublic;

        return !isItemLoaded(index) ? (
            <ActivityListSkeleton even={index % 2 === 0} />
        ) : (
            <div
                className="flex flex-justify-space-between list-item-row"
                onClick={() => handleActivitySelect(activity?.userId, activity?.activityInstanceId)}
            >
                <table className={"activity-list-table"} style={{ width: "100%" }}>
                    <tbody>
                        <tr>
                            <td className={"icon"}>
                                <ActivityIcon categoryName={activity?.categoryName} size={24} />
                            </td>
                            <td className={"label"}>
                                <div>
                                    <div>{activity?.activityLabel}</div>
                                    {filter !== "Your Activities" && (
                                        <div style={{ width: "100%", fontSize: "12px", color: "#777777" }}>
                                            {activity?.userFullName}
                                        </div>
                                    )}
                                </div>
                                <VideoTag mediaStatus={activity?.mediaStatus} />
                            </td>
                            <td className={"status"}>
                                <div className="flex">
                                    {
                                        <CMEStatus
                                            info={
                                                isCMEPublic || isOwner
                                                    ? associatedStatusesMap.get(activity?.activityInstanceId)
                                                    : null
                                            }
                                        />
                                    }
                                </div>
                            </td>
                            <td className={"date"}>
                                <div style={{ width: "100%" }}>
                                    {moment(activity?.creationDate).format("MMM Do YYYY")}
                                </div>
                                <div style={{ width: "100%", fontSize: "12px", color: "#777777" }}>
                                    {moment(activity?.creationDate).format("h:mm A")}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div
            ref={parentRef}
            className="activity-list"
            style={{
                height: `calc(100vh - 400px)`,
                width: `100%`,
                overflowY: "scroll",
                overflowX: "hidden",
            }}
        >
            {totalLength ? (
                <div
                    style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        width: "100%",
                        position: "relative",
                    }}
                >
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        return (
                            <div
                                key={virtualRow.index}
                                className={
                                    virtualRow.index % 2 ? "list-item list-item-odd" : "list-item list-item-even"
                                }
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: `${virtualRow.size}px`,
                                    transform: `translateY(${virtualRow.start}px)`,
                                }}
                            >
                                {RenderItem(virtualRow.index)}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <Empty description={"No results to display."} style={{ paddingBottom: "40px", marginTop: "20px" }} />
            )}
        </div>
    );
};

export default memo(InfiniteActivityList);
